import React from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import Badge from "../../../components/ui-components/Badge";
import { ROLE_LABELS } from "../policyBuilderState";

const resolveUser = (userId, usersMap) => {
  if (!userId || !usersMap) return { name: userId || "", title: "" };
  const u = usersMap[String(userId)];
  if (!u) return { name: userId, title: "" };
  const parts = [u.first_name, u.last_name].filter(Boolean);
  const name = parts.length > 0 ? parts.join(" ") : userId;
  return { name, title: u.function || "" };
};

/**
 * Derives the display name of the actor for an approval step.
 * Current step: assigned_approver / approver_user_id / active_approver.
 * Completed step: decided_by / approved_by / rejected_by / approver_user_id.
 */
function resolveApprovalStepActor(step, usersMap) {
  if (!usersMap) return null;
  const status = step.status;
  const userId =
    status === "approved" || status === "rejected"
      ? step.decided_by || step.approver_user_id
      : step.approver_user_id ||
        step.assigned_approver ||
        step.active_approver ||
        step.current_actor ||
        step.current_reviewer;
  if (!userId) return null;
  const u = resolveUser(userId, usersMap);
  return u.name || null;
}

/**
 * Step title: actual stage name from step definition / policy chain / role.
 * Prefer step.label, then policy snapshot chain by position (index), then step.role (ROLE_LABELS or humanized) + " Approval".
 * Fall back to "Approval" only when no better label exists.
 * Uses index for chain lookup; chain is chosen from the tier whose chain length matches stepsCount (the tier used for this request).
 */
function getStepTitle(step, policySnapshot, index, stepsCount) {
  if (step.label && String(step.label).trim()) return step.label.trim();

  const chain = getChainFromPolicySnapshot(policySnapshot, stepsCount);
  const link = chain[index];
  if (link && typeof link === "object") {
    const rawLabel = link.label ?? link.Label;
    const rawRole = link.role ?? link.Role;
    let label = null;
    if (rawLabel && String(rawLabel).trim()) {
      label = String(rawLabel).trim();
    } else if (rawRole) {
      label = ROLE_LABELS[rawRole] || humanizeRoleKey(String(rawRole));
    }
    if (label) return label;
  }

  const role = step.role || step.approver_role;
  if (role) {
    const roleLabel = ROLE_LABELS[role] || humanizeRoleKey(role);
    return `${roleLabel} Approval`;
  }

  return "Approval";
}

/** Get chain array from a tier object. */
function getTierChain(tier) {
  const chain = tier?.chain ?? tier?.Chain;
  return Array.isArray(chain) ? chain : [];
}

/** Get approval chain array from policy snapshot.
 * When stepsCount is given, prefers the tier whose chain length matches (the tier that was used for this request).
 * Otherwise uses first tier's chain or top-level chain/steps.
 */
function getChainFromPolicySnapshot(policySnapshot, stepsCount) {
  if (!policySnapshot || typeof policySnapshot !== "object") return [];
  const tiers = policySnapshot.tiers || policySnapshot.Tiers;
  if (Array.isArray(tiers) && tiers.length > 0) {
    if (typeof stepsCount === "number" && stepsCount > 0) {
      const matchingTier = tiers.find((t) => getTierChain(t).length === stepsCount);
      if (matchingTier) return getTierChain(matchingTier);
    }
    const firstChain = getTierChain(tiers[0]);
    if (firstChain.length > 0) return firstChain;
  }
  const topChain = policySnapshot.chain || policySnapshot.Chain;
  if (Array.isArray(topChain)) return topChain;
  const steps = policySnapshot.steps || policySnapshot.Steps;
  return Array.isArray(steps) ? steps : [];
}

/** Turn role key into display text (e.g. budget_owner → Budget owner). */
function humanizeRoleKey(key) {
  if (!key || typeof key !== "string") return "";
  return key
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Progression state: completed (approved/rejected), current (first pending), future (not yet reached).
 */
function getProgressionState(step, index, firstPendingIndex) {
  if (step.status === "approved" || step.status === "rejected") return "completed";
  if (step.status === "pending" && index === firstPendingIndex) return "current";
  return "future";
}

/** Subline text for a completed step (Approved/Rejected by actor). */
function getCompletedSubline(step, actorName) {
  if (step.status === "approved") return actorName ? `Approved by ${actorName}` : "Approved";
  if (step.status === "rejected") return actorName ? `Rejected by ${actorName}` : "Rejected";
  return null;
}

/**
 * Format completion timestamp for audit display.
 * Today → time only (e.g. 14:32).
 * Not today → date + time (e.g. 9 Mar 2026, 14:32).
 * Prior year → include year (e.g. 18 Dec 2025, 09:10).
 * Uses en-GB-style locale for consistency with RequesterPanel.
 */
function formatCompletionTimestamp(isoString) {
  if (!isoString) return "";
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) {
    return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  }
  const dateStr = d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: d.getFullYear() === now.getFullYear() ? undefined : "numeric",
  });
  const timeStr = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  return `${dateStr}, ${timeStr}`;
}

/**
 * Vertical timeline of approval steps.
 * Decision-centric: marker = progression (completed/current/future); pill = semantic status.
 * Reuse: ApprovalProgressPanel, ApprovalRequestDetailPage.
 *
 * @param {{ steps: object[], usersMap?: object, policySnapshot?: object }} props
 */
const ApprovalStepTimeline = ({ steps, usersMap, policySnapshot }) => {
  if (!steps || steps.length === 0) {
    return (
      <div className="text-sm text-text-2">No steps</div>
    );
  }

  const sortedSteps = [...steps].sort((a, b) => (a.step_index ?? a.id ?? 0) - (b.step_index ?? b.id ?? 0));
  const firstPendingIndex = sortedSteps.findIndex((s) => s.status === "pending");

  return (
    <div className="space-y-0">
      {sortedSteps.map((step, idx) => {
        const isLast = idx === sortedSteps.length - 1;
        const progression = getProgressionState(step, idx, firstPendingIndex);
        const isCurrent = progression === "current";
        const isCompleted = progression === "completed";
        const isFuture = progression === "future";

        const actorName = resolveApprovalStepActor(step, usersMap);
        const stepTitle = getStepTitle(step, policySnapshot, idx, sortedSteps.length);

        let pillStatus = step.status;
        let pillLabel = null;
        if (step.status === "approved") pillLabel = "Approved";
        else if (step.status === "rejected") pillLabel = "Rejected";
        else if (isCurrent) {
          pillStatus = "awaiting";
          pillLabel = "Awaiting decision";
        } else if (isFuture) {
          pillStatus = "pending";
          pillLabel = "Pending";
        }
        const badgeLabel = pillLabel == null ? undefined : pillLabel;

        const previousStepIsCurrent = idx > 0 && firstPendingIndex === idx - 1;
        const addSpaceAbove = previousStepIsCurrent;

        return (
          <div
            key={step.id ?? idx}
            className={`flex gap-4 ${isCurrent ? "rounded-ctrl border border-border bg-surface-2 pl-2 pr-3 pt-3 pb-0" : ""} ${addSpaceAbove ? "pt-3" : ""}`}
          >
            {/* Left column: marker (8–10px) + 1px neutral connector */}
            <div className="flex flex-col items-center shrink-0 w-5">
              {isCompleted && (
                <div
                  className="rounded-full ml-2 shrink-0 flex items-center justify-center border border-success  text-success mt-3"
                  style={{ width: 0, height: 0 }}
                  aria-hidden
                >
                  <FontAwesomeIcon icon={icon({ name: "check", family: "sharp", style: "regular" })} className="text-[12px]" />
                </div>
              )}
              {isCurrent && (
                <div
                  className="rounded-full shrink-0 mt-3 bg-primary-soft border border-primary"
                  style={{ width: 10, height: 10 }}
                  aria-hidden
                />
              )}
              {isFuture && (
                <div
                  className="rounded-full shrink-0 mt-3 ml-2 border border-border bg-card"
                  style={{ width: 10, height: 10 }}
                  aria-hidden
                />
              )}
              {isLast ? null : (
                <div
                  className="w-px flex-1 min-h-[24px] bg-border mt-0.5"
                  style={{ minHeight: 24 }}
                  aria-hidden
                />
              )}
            </div>

            {/* Right column: step content */}
            <div
              className={`flex-1 pb-4 min-w-0 ${!isCurrent ? "pr-3" : ""}`}
              aria-label={[stepTitle, badgeLabel, isCompleted && step.decided_at ? formatCompletionTimestamp(step.decided_at) : null]
                .filter(Boolean)
                .join(", ")}
            >
              {/* First row: step title, pill, optional timestamp (completed only) */}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className={`text-sm text-text ${isCurrent ? "font-semibold" : "font-normal"}`}>
                  {stepTitle}
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  <Badge status={pillStatus} label={badgeLabel} />
                  {isCompleted && step.decided_at && (
                    <span className="text-xs text-text-2 whitespace-nowrap" aria-label={`Decided at ${formatCompletionTimestamp(step.decided_at)}`}>
                      {formatCompletionTimestamp(step.decided_at)}
                    </span>
                  )}
                </span>
              </div>

              {/* Second row: actor / state subline (no timestamp here) */}
              {isCurrent && (
                <p className="text-xs text-text-2 mt-0.5">
                  {actorName ? `Awaiting decision from ${actorName}` : "Awaiting decision"}
                </p>
              )}
              {isCompleted && (
                <p className="text-xs text-text-2 mt-0.5">
                  {getCompletedSubline(step, actorName)}
                </p>
              )}
              {isFuture && (
                <p className="text-xs text-text-2 mt-0.5">Not yet started</p>
              )}

              {step.acted_as_delegate && (() => {
                const delegator = step.delegator_user_id ? resolveUser(step.delegator_user_id, usersMap) : null;
                return delegator?.name ? (
                  <div className="text-xs text-primary font-medium mt-0.5">
                    Acted as delegate for {delegator.name}
                  </div>
                ) : null;
              })()}

              {step.comment && (
                <div className="mt-2 text-sm text-text bg-surface-2 rounded p-2">
                  {step.comment}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

ApprovalStepTimeline.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      step_index: PropTypes.number,
      label: PropTypes.string,
      role: PropTypes.string,
      approver_role: PropTypes.string,
      status: PropTypes.string,
      approver_user_id: PropTypes.string,
      decided_at: PropTypes.string,
      decided_by: PropTypes.string,
      comment: PropTypes.string,
      acted_as_delegate: PropTypes.bool,
      delegator_user_id: PropTypes.string,
    })
  ),
  usersMap: PropTypes.object,
  policySnapshot: PropTypes.object,
};

export default ApprovalStepTimeline;
